'use client';

import { useState, useCallback, useRef, useEffect } from 'react';
import { Send, Loader2, Bot, Sparkles, ChevronDown, Check, X, Network, FileSpreadsheet, Ruler, Landmark, Palette, CircleDollarSign, ThumbsUp, ThumbsDown, CalendarPlus, Workflow, Copy } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';

import remarkGfm from 'remark-gfm';
import { useChatStore, Message } from '../lib/useChatStore';

/**
 * OmniChatPanel — 통합 채팅 패널 (모든 페이지 공통)
 *
 * 기능:
 * 1. omni-chat API 호출 (SSE 스트리밍 없이 단순 POST)
 * 2. VE 분석 결과 자동 감지 → 구조화 카드 렌더링
 * 3. NAS 파일 참조 경로 마크다운 링크
 * 4. 에이전트 전환 (omni/boq/cad/btl...)
 * 5. 대화 히스토리 유지
 */

// Message interface moved to useChatStore.ts
interface OmniChatPanelProps {
  defaultAgent?: string;
  projectId?: string;
  taskTitle?: string;
  selectedFile?: string;
  channel?: string;
  placeholder?: string;
  className?: string;
  compact?: boolean;  // 사이드바 모드
  externalNasPath?: string | null;
  onNasPathLoaded?: () => void;
  onRequestNasBrowser?: () => void;
}

export default function OmniChatPanel({
  defaultAgent = 'omni',
  projectId,
  taskTitle,
  selectedFile,
  channel = 'web',
  placeholder = '건축 설계 관련 무엇이든 질문하세요...',
  className = '',
  compact = false,
  externalNasPath,
  onNasPathLoaded,
  // _onRequestNasBrowser,
}: OmniChatPanelProps) {
  const effectiveProjectId = projectId || 'default';
  const getMessages = useChatStore(s => s.getMessages);
  const setMessages = useChatStore(s => s.setMessages);
  const messages = getMessages(effectiveProjectId);
  
  const [input, setInput] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agent, setAgent] = useState(defaultAgent);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const isComposing = useRef(false);  // 한글 IME 조합 중 플래그

  // ── 추가: 붙여넣기된 이미지 ──
  const [pastedImages, setPastedImages] = useState<string[]>([]);

  // --- NEW UX MOMENT STATES ---
  const [readReceiptId, setReadReceiptId] = useState<string | null>(null);
  const [loadingText, setLoadingText] = useState('분석 중...');
  const [optimisticMsg, setOptimisticMsg] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopy = useCallback((id: string, content: string) => {
    navigator.clipboard.writeText(content);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  }, []);


  // --- 피드백 시스템 상태 ---
  const [feedbackSubmitted, setFeedbackSubmitted] = useState<Record<string, boolean>>({});
  const [feedbackLoading, setFeedbackLoading] = useState<string | null>(null);
  // const [feedbackSuggestions, setFeedbackSuggestions] = useState<string[]>([]);
  
  useEffect(() => {
    if (externalNasPath && onNasPathLoaded) {
      onNasPathLoaded();
    }
  }, [externalNasPath, onNasPathLoaded]);

  const AGENTS = [
    { value: 'omni', label: '통합 RAG', icon: Network, color: 'text-blue-500' },
    { value: 'boq', label: 'BOQ 견적', icon: FileSpreadsheet, color: 'text-amber-500' },
    { value: 'cad', label: 'CAD 도면', icon: Ruler, color: 'text-cyan-500' },
    { value: 'btl', label: '공공건축', icon: Landmark, color: 'text-emerald-500' },
    { value: 'design', label: '설계 검토', icon: Palette, color: 'text-rose-500' },
    { value: 'cost', label: '원가 관리', icon: CircleDollarSign, color: 'text-yellow-500' },
    { value: 'openclaw', label: 'OpenClaw 코드', icon: Bot, color: 'text-red-500' },
  ];

  const currentAgentData = AGENTS.find(a => a.value === agent) || AGENTS[0];

  // 과거 대화 히스토리 불러오기 (프로젝트가 변경되거나 처음 렌더링될 때)
  useEffect(() => {
    if (messages.length === 0) {
      setLoading(true);
      fetch(`/api/omni-chat/history?projectId=${encodeURIComponent(effectiveProjectId)}`)
        .then(res => res.json())
        .then(data => {
            if (data.history && data.history.length > 0) {
                const formatted = data.history.map((h: any) => ({
                    id: h.id,
                    role: h.role,
                    content: h.content,
                    timestamp: h.timestamp,
                    meta: { projectId: effectiveProjectId }
                }));
                // 과거 메시지는 이미 서버에서 오래된 순 -> 최신순 정렬됨
                setMessages(effectiveProjectId, formatted);
            }
        })
        .catch(err => console.error("Failed to fetch history:", err))
        .finally(() => setLoading(false));
    }
  }, [effectiveProjectId]); // 의도적으로 messages.length 의존성 제외

  // UX 모먼트: 지능형 로딩 텍스트 (Thinking 과정 시뮬레이션)
  useEffect(() => {
    if (!loading) {
      setLoadingText('분석 중...');
      return;
    }
    const texts = [
      agent === 'openclaw' ? '코드베이스 스캔 중...' : 'NAS 03_DWG 등 인덱스 접근 중...',
      agent === 'boq' ? '내역서 엑셀 셀 파싱 중...' : 'L2/L3 도서세트 버전 비교 중...',
      '문서 텍스트 집중 분석 중...',
      '휴.. 양이 꽤 되네요 💦',
      '답변 생성 중...'
    ];
    let i = 0;
    setLoadingText(texts[0]);
    const interval = setInterval(() => {
      i += 1;
      if (i >= texts.length) {
          clearInterval(interval);
      } else {
          setLoadingText(texts[i]);
      }
    }, 1500);
    return () => clearInterval(interval);
  }, [loading, agent]);

  // 자동 스크롤
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, optimisticMsg, loadingText]);

  const sendMessage = useCallback(async () => {
    if ((!input.trim() && pastedImages.length === 0) || loading) return;
    const currentImages = pastedImages;
    const userMsg: Message = {
      id: `msg_${Date.now()}_user`,
      role: 'user',
      content: input.trim() || '(이미지 전송됨)',
      timestamp: Date.now(),
      images: currentImages.length > 0 ? currentImages : undefined,
    };
    setPastedImages([]);
    setMessages(effectiveProjectId, prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    // UX 모먼트: 1 읽음 표시 및 낙관적 분할 응답
    setReadReceiptId(userMsg.id);
    const optimismTimer = setTimeout(() => {
        setReadReceiptId(null);
        let thoughtStream = "데이터망 속에서 지식 구조를 파싱하고 있습니다...";
        if (selectedFile) {
            const fileName = selectedFile.split('/').pop() || '';
            const isPdf = fileName.toLowerCase().endsWith('.pdf');
            const isExcel = fileName.toLowerCase().includes('.xls');
            const isImg = fileName.toLowerCase().match(/\\.(png|jpg|jpeg|gif)$/);
            const isCompare = input.toLowerCase().includes('비교') || input.toLowerCase().includes('증감');
            
            if (isPdf && isCompare) thoughtStream = `[🧠 멀티모달] 📄 2개 이상의 PDF 내역서를 시각적으로 읽고 비교 분석 중입니다 (초대형 컨텍스트 처리 - 최대 3~4분 소요)...`;
            else if (isPdf) thoughtStream = `[스캔] 📄 ${fileName} PDF 형식을 분석하고 멀티모달 비전 엔진으로 읽는 중입니다...`;
            else if (isExcel) thoughtStream = `[분석] 📊 ${fileName} 내역서 데이터의 계산식을 추적하고 셀 구조를 정규화 중입니다...`;
            else if (isImg) thoughtStream = `[비전] 🖼️ ${fileName} 이미지 데이터에서 시각적 토큰을 추출하고 공간 패턴을 추론 중입니다...`;
            else thoughtStream = `[읽기] 📁 NAS 스토리지에서 ${fileName} 문서 메타데이터를 인덱싱하고 있습니다...`;
        } else if (input.toLowerCase().includes('비교') && input.toLowerCase().includes('내역서')) {
            thoughtStream = `[🧠 멀티모달] 📄 문서 간의 비교 분석을 위해 로컬 추론을 시작했습니다... (잠시 대기해주세요)`;
        } else if (input.toLowerCase().includes('일정') || input.toLowerCase().includes('보고')) {
            thoughtStream = `[조회] 📅 PostgreSQL DB에서 프로젝트 ${effectiveProjectId || ''}의 전체 마일스톤 및 태스크를 동기화 중입니다...`;
        } else {
            thoughtStream = `[탐색] 🔍 질문의 의도를 분석하고 OMNI 시맨틱 캐시 데이터를 확인 중입니다...`;
        }
        setOptimisticMsg(thoughtStream);
    }, 400);

    try {
      const res = await fetch('/api/omni-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [
            // 최근 10턴 대화 히스토리 (멀티턴 컨텍스트)
            ...messages.slice(-10).map(m => ({ role: m.role, content: m.content, images: m.images })),
            { role: 'user' as const, content: userMsg.content, images: userMsg.images },
          ],
          metadata: {
            selectedFile,
            projectId,
            taskTitle,
            channel,
            agent,
          },
        }),
      });

      let data: any;
      try {
        data = await res.json();
      } catch (parseErr) {
        console.debug('JSON 파싱 에러 (무시):', parseErr);
        const textBody = await res.text().catch(() => '');
        data = { answer: null, error: `응답 파싱 오류: ${textBody.slice(0, 200) || '서버 응답을 처리할 수 없습니다'}` };
      }
      const aiMsg: Message = {
        id: `msg_${Date.now()}_ai`,
        role: 'assistant',
        content: data.answer || data.error || '응답을 받지 못했습니다.',
        timestamp: Date.now(),
        meta: {
          elapsed: data.elapsed,
          source: data.source,
          projectId: data.projectId,
        },
      };
      setMessages(effectiveProjectId, prev => [...prev, aiMsg]);
    } catch (e: any) {
      setMessages(effectiveProjectId, prev => [...prev, {
        id: `msg_${Date.now()}_err`,
        role: 'assistant',
        content: `오류: ${e.message}`,
        timestamp: Date.now(),
      }]);
    } finally {
      clearTimeout(optimismTimer);
      setReadReceiptId(null);
      setOptimisticMsg(null);
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [input, loading, selectedFile, projectId, channel, agent, effectiveProjectId, messages, setMessages]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // 한글 IME 조합 중이면 Enter 무시 (마지막 글자 중복 방지)
    if (e.nativeEvent.isComposing || isComposing.current) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }, [sendMessage]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
            const file = items[i].getAsFile();
            if (!file) continue;
            
            const reader = new FileReader();
            reader.onload = (event) => {
                if (event.target?.result) {
                    setPastedImages(prev => [...prev, event.target!.result as string]);
                }
            };
            reader.readAsDataURL(file);
        }
    }
  }, []);

  // --- 피드백 제출 핸들러 ---
  const handleFeedback = useCallback(async (messageId: string, rating: number) => {
    if (feedbackSubmitted[messageId] || feedbackLoading) return;

    setFeedbackLoading(messageId);
    try {
      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: effectiveProjectId,
          messageId,
          rating, // 1: positive, -1: negative
        }),
      });

      if (res.ok) {
        setFeedbackSubmitted(prev => ({ ...prev, [messageId]: true }));

        // 부정 피드백인 경우 개선 제안 분석
        if (rating < 0) {
          const analyzeRes = await fetch('/api/feedback/analyze', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              projectId: effectiveProjectId,
              messageId,
              rating,
            }),
          });
          if (analyzeRes.ok) {
            // const data = await analyzeRes.json();
            // setFeedbackSuggestions(data.suggestions || []);
          }
        }
      }
    } catch (error) {
      console.error('[Feedback] Failed:', error);
    } finally {
      setFeedbackLoading(null);
    }
  }, [effectiveProjectId, feedbackSubmitted, feedbackLoading]);

  return (
    <div 
      className={`relative flex flex-col bg-white dark:bg-[#0a0a12] border ${isDragOver ? 'border-cyan-500 shadow-[0_0_0_2px_rgba(6,182,212,0.5)]' : 'border-gray-200 dark:border-white/10'} rounded-2xl overflow-hidden transition-all ${compact ? 'h-[500px]' : 'h-[700px]'} ${className}`}
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragOver(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        setIsDragOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragOver(false);
        // 1. Native OS files
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
          const fnames = files.map(f => f.name).join(', ');
          setInput(prev => prev + (prev ? '\n' : '') + `[첨부된 파일: ${fnames}] `);
          return;
        }
        // 2. NAS files dropped from Center Explorer
        let nasDataStr = e.dataTransfer.getData('application/json');
        if (!nasDataStr) {
          nasDataStr = e.dataTransfer.getData('text/plain');
        }
        if (nasDataStr) {
          try {
            const data = JSON.parse(nasDataStr);
            if (data.type === 'nas_file' || data.type === 'nas_folder') {
              setInput(prev => prev + (prev ? '\n' : '') + `[선택된 NAS 경로: ${data.path}] `);
            }
          } catch (err) {
            console.debug('드래그앤드롭 파싱 예외:', err);
          }
        }
      }}
    >
      {isDragOver && (
        <div className="absolute inset-0 z-[100] flex items-center justify-center bg-cyan-500/10 backdrop-blur-sm pointer-events-none">
          <div className="bg-white dark:bg-[#111118] px-6 py-4 rounded-2xl shadow-xl shadow-cyan-500/20 border border-cyan-500/30 flex flex-col items-center gap-3 animate-pulse">
            <div className="w-12 h-12 rounded-full bg-cyan-50 dark:bg-cyan-500/20 flex items-center justify-center">
              <Bot className="w-6 h-6 text-cyan-500" />
            </div>
            <p className="text-sm font-bold text-cyan-600 dark:text-cyan-400">여기에 파일을 내려놓으세요</p>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/[0.02]">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
            <Bot className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900 dark:text-white">OpenClaw Gateway (Poby)</h3>
            <p className="text-[10px] text-gray-500 dark:text-gray-500">
              {projectId ? `프로젝트: ${projectId}` : 'NAS 기반 AI 어시스턴트'}
            </p>
          </div>
        </div>
        {/* Agent 및 도구 영역 */}
        <div className="flex items-center gap-2">

          
          <div className="relative">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-1.5 text-[10px] bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg px-2.5 py-1.5 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/10 transition-colors focus:outline-none"
            >
              <currentAgentData.icon className={`w-3.5 h-3.5 ${currentAgentData.color}`} />
              <span className="font-semibold">{currentAgentData.label}</span>
              <ChevronDown className="w-3 h-3 text-gray-400" />
            </button>
          
          <AnimatePresence>
            {isDropdownOpen && (
              <>
                <div 
                  className="fixed inset-0 z-40"
                  onClick={() => setIsDropdownOpen(false)}
                />
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -10 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 top-full mt-1 w-36 bg-white dark:bg-gray-800 border border-gray-200 dark:border-white/10 rounded-xl shadow-xl z-50 overflow-hidden"
                >
                  <div className="p-1 flex flex-col gap-0.5">
                    {AGENTS.map((a) => (
                      <button
                        key={a.value}
                        onClick={() => { setAgent(a.value); setIsDropdownOpen(false); }}
                        className={`flex items-center gap-2 px-3 py-2 text-[11px] rounded-lg transition-colors text-left w-full ${
                          agent === a.value 
                            ? 'bg-blue-50 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 font-bold' 
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 font-medium'
                        }`}
                      >
                        <a.icon className={`w-3.5 h-3.5 ${a.color}`} />
                        <span>{a.label}</span>
                      </button>
                    ))}
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
        </div>
      </div>



      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-600">
            <Sparkles className="w-8 h-8 mb-3 text-cyan-500/30" />
            <p className="text-xs text-center">
              {selectedFile
                ? `선택된 파일: ${selectedFile.split('/').pop()}`
                : '건축설계 전문 AI가 NAS 실데이터를 기반으로 답변합니다'}
            </p>
            <div className="flex flex-wrap gap-2 mt-4 max-w-sm justify-center">
              {[
                '교육장실 경량천장 공사비?',
                '복도 포세린타일을 LVT로 교체하면?',
                '이 프로젝트 도면 목록',
                '프로젝트 구조 분석해줘',
                '최근 커밋 내역 알려줘',
              ].map((q, i) => (
                <button
                  key={i}
                  onClick={() => { setInput(q); inputRef.current?.focus(); }}
                  className="text-[10px] px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white transition-all"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg) => {
          // 불필요한 다중 공백/개행 제거 (3줄 이상의 빈 줄을 2줄로 축소)
          const cleanContent = msg.content
            .replace(/\r\n/g, '\n')           // Windows 줄바꿈 통일
            .replace(/\n{3,}/g, '\n\n')        // 3줄 이상 공백 → 2줄로
            .replace(/[ \t]+\n/g, '\n')        // 줄 끝 공백 제거
            .replace(/^\s+|\s+$/g, '');        // 앞뒤 공백 제거
          // 마크다운 랜더 전 NAS 경로 링크 변환
          const linkedContent = autoLinkify(
            cleanContent.replace(/(?<!\]\()B_DESIGN\/([^\s\n<]+)/g, (match) => {
              return `[${match}](/live?path=${encodeURIComponent(match)})`;
            })
          );
          return (
          <div
            key={msg.id}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 relative group ${
              msg.role === 'user'
                ? 'bg-cyan-50 dark:bg-cyan-600/20 text-cyan-900 dark:text-cyan-100 rounded-br-md border border-cyan-100 dark:border-cyan-500/10'
                : 'bg-gray-50 dark:bg-white/5 text-gray-800 dark:text-gray-300 rounded-bl-md border border-gray-100 dark:border-white/5'
            }`}>
              {msg.id === readReceiptId && (
                <span className="absolute -left-3 bottom-1.5 text-yellow-500 dark:text-yellow-400 text-[10px] font-bold">
                  1
                </span>
              )}
              {msg.role === 'assistant' && msg.content.includes('[💰 VE 분석 모드]') ? (
                <VEResultCard content={linkedContent} />
              ) : msg.role === 'assistant' && msg.content.includes('[🦞 OpenClaw') ? (
                <OpenClawResultCard content={linkedContent} />
              ) : (
                <div className="text-[12px] leading-relaxed whitespace-pre-wrap">
                  {renderMarkdown(linkedContent)}
                  {msg.images && msg.images.length > 0 && (
                      <div className="flex gap-2 mt-2 flex-wrap">
                          {msg.images.map((img, i) => (
                              <img key={i} src={img} alt="attached" className="max-w-[200px] max-h-[200px] rounded flex-shrink-0 object-contain border border-gray-200 dark:border-white/10" />
                          ))}
                      </div>
                  )}
                </div>
              )}
              {/* 복사 버튼 — 메시지 우상단 항상 표시 */}
              <div className={`flex items-center justify-between mt-1.5`}>
                <div className="flex-1" />
                <button 
                  onClick={() => handleCopy(msg.id, msg.content)}
                  title="복사"
                  className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] transition-all ${
                    copiedId === msg.id
                      ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                      : 'bg-gray-100 dark:bg-white/5 text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-white/10'
                  }`}
                >
                  {copiedId === msg.id ? (
                    <><Check className="w-3 h-3" /><span>복사됨</span></>
                  ) : (
                    <><Copy className="w-3 h-3" /><span>복사</span></>
                  )}
                </button>
              </div>
              {msg.meta?.elapsed && (
                <div className="flex items-center justify-between text-[9px] text-gray-500 dark:text-gray-400 mt-2 border-t border-gray-200 dark:border-white/10 pt-2">
                  <div className="flex items-center gap-1">
                    <span>{msg.meta.source} · {msg.meta.elapsed}</span>
                  </div>
                  {msg.role === 'assistant' && (
                    <div className="flex items-center gap-2.5">
                      <button 
                        onClick={() => {
                          fetch('/api/pipeline/submit', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ agentType: 'flow-master', projectId: effectiveProjectId, prompt: msg.content.substring(0, 100) })
                          }).then(() => alert('플로우 마스터(Shon)에게 파이프라인 생성 작업을 접수했습니다.'))
                            .catch(() => alert('네트워크 오류가 발생했습니다.'));
                        }} 
                        className="flex items-center gap-1 hover:text-emerald-500 transition-colors" 
                        title="워크플로우/플로우에 결과 등록"
                      >
                        <Workflow className="w-3 h-3" />
                        <span className="hidden sm:inline">플로우 연동</span>
                      </button>
                      <button 
                        onClick={() => {
                          fetch('/api/schedule/agent-action', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ action: 'createTask', title: msg.content.substring(0, 40) + '...', date: new Date().toISOString().split('T')[0], projectId: effectiveProjectId })
                          }).then(() => alert(`스케줄러 캘린더에 [${effectiveProjectId || '새 프로젝트'}] 관련 태스크로 등록되었습니다.`))
                            .catch(() => alert('네트워크 오류가 발생했습니다.'));
                        }} 
                        className="flex items-center gap-1 hover:text-amber-500 transition-colors" 
                        title="스케줄/공정관리 달력에 등록"
                      >
                        <CalendarPlus className="w-3 h-3" />
                        <span className="hidden sm:inline">스케줄 등록</span>
                      </button>
                      <div className="w-px h-3 bg-gray-300 dark:bg-white/20 mx-0.5"></div>
                      <button
                        onClick={() => handleFeedback(msg.id, 1)}
                        disabled={feedbackSubmitted[msg.id] || feedbackLoading === msg.id}
                        className={`transition-colors ${feedbackSubmitted[msg.id] ? 'text-blue-500' : 'hover:text-blue-500'}`}
                        title="훌륭한 답변입니다"
                      >
                        {feedbackLoading === msg.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ThumbsUp className="w-3 h-3" />}
                      </button>
                      <button
                        onClick={() => handleFeedback(msg.id, -1)}
                        disabled={feedbackSubmitted[msg.id] || feedbackLoading === msg.id}
                        className={`transition-colors ${feedbackSubmitted[msg.id] ? 'text-red-500' : 'hover:text-red-500'}`}
                        title="답변에 문제가 있습니다"
                      >
                        {feedbackLoading === msg.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ThumbsDown className="w-3 h-3" />}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          );
        })}

        {optimisticMsg && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="flex justify-start"
          >
            <div className="max-w-[85%] bg-gray-50 dark:bg-white/5 text-gray-800 dark:text-gray-300 rounded-2xl rounded-bl-md px-4 py-2.5 border border-gray-100 dark:border-white/5">
              <div className="text-[12px] leading-relaxed">
                {optimisticMsg}
              </div>
            </div>
          </motion.div>
        )}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex items-center gap-2 text-cyan-500 dark:text-cyan-400">
                <Loader2 className="w-3 h-3 animate-spin" />
                <span className="text-[11px]">{loadingText}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="border-t border-gray-200 dark:border-white/10 bg-white dark:bg-[#0a0a12] p-3">
        {pastedImages.length > 0 && (
          <div className="flex gap-2 mb-2 flex-wrap">
            {pastedImages.map((img, i) => (
              <div key={i} className="relative w-16 h-16 rounded-md overflow-hidden border border-gray-200 dark:border-white/10 group">
                <img src={img} alt="pasted" className="w-full h-full object-cover" />
                <button
                  onClick={() => setPastedImages(prev => prev.filter((_, idx) => idx !== i))}
                  className="absolute top-1 right-1 bg-black/60 hover:bg-black p-1 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onCompositionStart={() => { isComposing.current = true; }}
            onCompositionEnd={() => { isComposing.current = false; }}
            placeholder={placeholder}
            rows={1}
            className="flex-1 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl px-3 py-2.5 text-[12px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 outline-none focus:border-cyan-500/50 resize-y min-h-[44px] max-h-[500px]"
            style={{ overflowY: 'auto' }}
            onInput={(e) => {
              const t = e.currentTarget;
              // Resetting height allows scrollHeight to shrink, but it breaks CSS resize manual height.
              // To support both auto-expand up to a point AND manual CSS resize-y,
              // we only auto-expand if the user hasn't dragged it significantly larger.
              if (t.scrollHeight > t.clientHeight || t.clientHeight < 120) {
                  t.style.height = 'auto';
                  t.style.height = `${Math.min(t.scrollHeight, 400)}px`;
              }
            }}
          />
          <button
            onClick={sendMessage}
            disabled={loading || (!input.trim() && pastedImages.length === 0)}
            className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white disabled:opacity-30 hover:shadow-lg hover:shadow-cyan-500/20 transition-all shrink-0"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── OpenClaw 코드 분석 결과 카드 ──
function OpenClawResultCard({ content }: { content: string }) {
  // [🦞 OpenClaw ...] 헤더 추출
  const headerMatch = content.match(/\[🦞[^\]]*\]/);
  const header = headerMatch ? headerMatch[0] : '[🦞 OpenClaw 코드 분석]';
  const body = content.replace(header, '').trim();

  return (
    <div className="space-y-2">
      <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 border border-red-500/20 rounded-xl p-3">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-sm">🦞</span>
          <span className="text-[11px] font-bold text-red-600 dark:text-red-300">OpenClaw 코드 분석</span>
          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-500/10 text-red-500 dark:text-red-400 font-mono">Phase 2</span>
        </div>
        <div className="text-[11px] leading-relaxed">
          {renderMarkdown(body)}
        </div>
      </div>
    </div>
  );
}

// ── VE 분석 결과 카드 ──
function VEResultCard({ content }: { content: string }) {
  // VE 모드 섹션과 RAG 섹션 분리
  const parts = content.split('---');
  const veSection = parts[0] || '';
  const ragSection = parts.slice(1).join('---');

  return (
    <div className="space-y-3">
      <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl p-3">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-sm">💰</span>
          <span className="text-[11px] font-bold text-amber-600 dark:text-amber-300">VE 분석 결과</span>
        </div>
        <div className="text-[11px] leading-relaxed whitespace-pre-wrap text-amber-900 dark:text-amber-100/80">
          {renderMarkdown(veSection.replace('[💰 VE 분석 모드]', '').trim())}
        </div>
      </div>
      {ragSection.trim() && (
        <div className="text-[11px] leading-relaxed whitespace-pre-wrap text-gray-700 dark:text-gray-400">
          {renderMarkdown(ragSection.trim())}
        </div>
      )}
    </div>
  );
}

// ── 마크다운 렌더링 (react-markdown + GFM) ──
function renderMarkdown(text: string) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ children }) => <h3 className="text-sm font-bold text-gray-900 dark:text-white mt-1.5 mb-0.5">{children}</h3>,
        h2: ({ children }) => <h4 className="text-[11px] font-bold text-gray-800 dark:text-gray-200 mt-1 mb-0.5">{children}</h4>,
        h3: ({ children }) => <h4 className="text-[11px] font-bold text-gray-800 dark:text-gray-200 mt-1 mb-0">{children}</h4>,
        p: ({ children }) => {
            const isEmpty = !children || 
                (Array.isArray(children) && children.every(c => typeof c === 'string' && c.trim() === '')) || 
                (typeof children === 'string' && children.trim() === '');
            if (isEmpty) return null;
            return <p className="text-[12px] text-gray-800 dark:text-gray-300 mb-0.5 leading-snug">{children}</p>;
        },
        strong: ({ children }) => <strong className="text-gray-900 dark:text-white font-semibold">{children}</strong>,
        em: ({ children }) => <em className="text-gray-700 dark:text-gray-400">{children}</em>,
        ul: ({ children }) => <ul className="pl-3 space-y-0 my-0.5 text-[12px]">{children}</ul>,
        ol: ({ children }) => <ol className="pl-4 space-y-0 my-0.5 list-decimal">{children}</ol>,
        li: ({ children }) => <li className="text-[11px] text-gray-800 dark:text-gray-300 leading-snug">{children}</li>,
        code: ({ children, className }) => {
          const isBlock = className?.includes('language-');
          if (isBlock) {
             const codeContent = String(children).trim();
             if (className?.includes('language-json')) {
                try {
                   const parsed = JSON.parse(codeContent);
                   if (parsed.type === 'human_approval') {
                      return <AgentApprovalCard data={parsed} />;
                   }
                } catch (err) {
                   console.debug('AgentApproval JSON parsing skipped:', err);
                   // Normal JSON string parse error, fallback to normal render
                }
             }

             return (
              <pre className="bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg p-2 overflow-x-auto my-1.5">
                <code className="text-[10px] font-mono text-cyan-700 dark:text-cyan-300">{children}</code>
              </pre>
             );
          }
          return <code className="bg-gray-200 dark:bg-white/10 px-1 py-0.5 rounded text-cyan-600 dark:text-cyan-300 text-[10px] font-mono">{children}</code>;
        },
        table: ({ children }) => (
          <div className="overflow-x-auto my-2">
            <table className="min-w-full text-[10px] border border-gray-200 dark:border-white/10 rounded-lg overflow-hidden">{children}</table>
          </div>
        ),
        thead: ({ children }) => <thead className="bg-gray-50 dark:bg-white/5">{children}</thead>,
        th: ({ children }) => <th className="px-2 py-1.5 text-left font-bold text-gray-900 dark:text-white border-b border-gray-200 dark:border-white/10">{children}</th>,
        td: ({ children }) => <td className="px-2 py-1 text-gray-700 dark:text-gray-300 border-b border-gray-100 dark:border-white/5">{children}</td>,
        hr: () => <hr className="border-gray-200 dark:border-white/10 my-2" />,
        a: ({ href, children }) => <a href={href} target="_blank" rel="noopener" className="text-cyan-600 dark:text-cyan-400 underline hover:text-cyan-500">{children}</a>,
        blockquote: ({ children }) => <blockquote className="border-l-2 border-cyan-500/30 pl-2 my-1 text-gray-600 dark:text-gray-400 italic">{children}</blockquote>,
      }}
    >{text}</ReactMarkdown>
  );
}

// ── URL/전화번호/유튜브 자동 하이퍼링크 변환 ──
function autoLinkify(text: string): string {
  // 이미 마크다운 링크 [...](...) 안에 있는 URL은 건드리지 않음
  // 유튜브 URL → [▶ YouTube](url) 아이콘 포함
  const youtubeRe = /(?<!\]\()(?<!\()https?:\/\/(?:www\.)?(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([\w-]{11})[^\s\n\)"']*/g;
  text = text.replace(youtubeRe, (url) => `[▶ YouTube](${url})`);

  // 일반 URL (http/https) — 이미 마크다운 링크 아닌 것만
  const urlRe = /(?<!\]\()(?<!\()https?:\/\/[^\s\n\)"'>]+/g;
  text = text.replace(urlRe, (url) => `[${url}](${url})`);

  // 전화번호 한국 형식: 010-XXXX-XXXX, 02-XXXX-XXXX, 0X0-XXX-XXXX 등
  const phoneRe = /(?<![0-9])(?:0(?:1[0-9]|2|3[1-9]|4[1-9]|5[1-9]|6[0-9]|7[0-9]|8[0-9]|9[0-9]))-?(\d{3,4})-?(\d{4})(?![0-9])/g;
  text = text.replace(phoneRe, (match) => {
    const digits = match.replace(/-/g, '');
    return `[📞 ${match}](tel:${digits})`;
  });

  return text;
}

// ── Agent 제안 검토 및 승인 뷰 (Suspendable Workflow) ──
function AgentApprovalCard({ data }: { data: any }) {
  const [status, setStatus] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const [loading, setLoading] = useState(false);

  const handleApprove = async () => {
    setLoading(true);
    try {
      if (data.toolCalls && data.toolCalls.length > 0) {
        for (const tc of data.toolCalls) {
          const actionBody = { action: 'createTask', ...tc.args, projectId: data.projectId || 'common' };
          await fetch('/api/schedule/agent-action', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(actionBody)
          });
        }
      }
      setStatus('approved');
    } catch (e) {
      console.error(e);
      setStatus('rejected');
    } finally {
      setLoading(false);
    }
  };

  return (
     <div className="bg-gradient-to-br from-indigo-500/10 to-blue-500/10 border border-indigo-500/20 rounded-xl p-3 my-3 shadow-sm">
        <div className="flex items-center justify-between mb-2 pb-2 border-b border-indigo-500/10">
            <div className="flex items-center gap-2">
                <Bot className="w-4 h-4 text-indigo-500" />
                <span className="text-[12px] font-bold text-indigo-700 dark:text-indigo-300">에이전트 자율 제안</span>
            </div>
            {status === 'pending' && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-600 animate-pulse font-bold">인간 승인 대기 (Human-in-the-loop)</span>}
            {status === 'approved' && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-600 font-bold">승인됨 및 DB 적용 완료</span>}
            {status === 'rejected' && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/20 text-red-600 font-bold">반려됨</span>}
        </div>
        
        <div className="space-y-2 mt-2">
           {data.toolCalls?.map((tc: any, i: number) => (
              <div key={i} className="bg-white/50 dark:bg-[#11131a] p-2.5 rounded-lg border border-gray-200 dark:border-gray-800 shadow-sm">
                 <div className="flex items-center gap-1.5 text-[10px] font-bold text-indigo-700 dark:text-indigo-400 mb-1">
                    <Sparkles className="w-3 h-3" />
                    {tc.toolName === 'generateMilestonesTool' ? '마일스톤 생성 툴' : tc.toolName === 'detectDelayTool' ? '지연 감지 툴' : tc.toolName}
                 </div>
                 <pre className="text-[9px] text-gray-600 dark:text-gray-400 overflow-x-auto whitespace-pre-wrap bg-gray-50 dark:bg-black/20 p-2 rounded">
                    {JSON.stringify(tc.args, null, 2)}
                 </pre>
              </div>
           ))}
        </div>

        {status === 'pending' && (
           <div className="flex items-center gap-2 mt-3 pt-3 border-t border-indigo-500/10">
              <button disabled={loading} onClick={handleApprove} className="flex-1 py-1.5 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 text-white text-[11px] font-bold rounded-lg transition-colors flex justify-center items-center gap-1.5 shadow-sm">
                 {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                 승인 (DB 즉시 반영)
              </button>
              <button disabled={loading} onClick={() => setStatus('rejected')} className="flex-1 py-1.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 text-gray-600 dark:text-gray-300 text-[11px] font-bold rounded-lg transition-colors flex justify-center items-center gap-1.5">
                 <X className="w-3.5 h-3.5" /> 반려 (거절)
              </button>
           </div>
        )}
     </div>
  );
}
